import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { fetchSession, login as apiLogin } from '../api/auth.ts';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { sessionUserFromResponse, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input } from '../ui/index.ts';
import { AUTH_LINK_CLASS, AuthPageFrame } from './auth/AuthPageFrame.tsx';
import { LoginCodeForm } from './auth/LoginCodeForm.tsx';

/**
 * Tenant sign-in — Dispatch Deck rebuild (epic #897 PR 8, formerly the
 * console-shared `createLogin` factory instantiation).
 *
 * Wire behavior preserved: local empty-field validation, then
 * `POST /v1/auth/login`, then a session probe to populate the context,
 * then redirect to `location.state.from` (guard bounce) or `/dashboard`.
 *
 * Errors are single-channel (epic AC 7): validation and submit failures
 * render inline only — the legacy inline+toast double-report is gone.
 *
 * Since epic #1475 the page carries two methods. Password stays the
 * default because it is what existing customers use; the email-code tab
 * is the ONLY door for invite-activated accounts, which have no password
 * at all. Both land in the same place — see `LoginCodeForm` for the
 * anti-enumeration rules the code flow has to keep.
 */

type LoginMode = 'password' | 'code';

function extractReturnTo(state: unknown): string | null {
  if (state === null || typeof state !== 'object') {
    return null;
  }
  const maybe = Reflect.get(state, 'from');
  if (typeof maybe !== 'string' || maybe.length === 0) {
    return null;
  }
  return maybe;
}

export function Login(): JSX.Element {
  const { status, setUser, fetchImpl } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [mode, setMode] = useState<LoginMode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === 'authenticated') {
    // Honor the guard bounce here too: react-router 7 runs navigate() inside
    // startTransition, so the post-login setUser re-render hits this guard
    // before the explicit navigate(returnTo) lands.
    const returnTo = extractReturnTo(location.state);
    return <Navigate to={returnTo !== null ? returnTo : '/dashboard'} replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0 || password.length === 0) {
      setError(t('login.error.empty_fields'));
      return;
    }
    setSubmitting(true);
    try {
      await apiLogin({ email: trimmedEmail, password }, fetchImpl);
      const session = await fetchSession(fetchImpl);
      setUser(sessionUserFromResponse(session));
      const returnTo = extractReturnTo(location.state);
      navigate(returnTo !== null ? returnTo : '/dashboard', { replace: true });
    } catch (err) {
      setError(coerceErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function goAfterSignIn(): void {
    const returnTo = extractReturnTo(location.state);
    navigate(returnTo !== null ? returnTo : '/dashboard', { replace: true });
  }

  return (
    <AuthPageFrame
      title={t('login.subtitle')}
      description={t('login.description')}
      footer={
        <>
          <Link to="/register" data-testid="login-register-link" className={AUTH_LINK_CLASS}>
            {t('login.need_account')}
          </Link>
          <Link to="/activate" data-testid="login-activate-link" className={AUTH_LINK_CLASS}>
            {t('login.have_invite_code')}
          </Link>
          <Link to="/forgot-password" data-testid="login-forgot-link" className={AUTH_LINK_CLASS}>
            {t('login.forgot')}
          </Link>
        </>
      }
    >
      <div
        className="mb-5 flex gap-2"
        role="group"
        aria-label={t('login.mode.label')}
        data-testid="login-mode-switch"
      >
        <Button
          variant={mode === 'password' ? 'secondary' : 'ghost'}
          aria-pressed={mode === 'password'}
          onClick={() => {
            setMode('password');
            setError(null);
          }}
          data-testid="login-mode-password"
          className="flex-1"
        >
          {t('login.mode.password')}
        </Button>
        <Button
          variant={mode === 'code' ? 'secondary' : 'ghost'}
          aria-pressed={mode === 'code'}
          onClick={() => {
            setMode('code');
            setError(null);
          }}
          data-testid="login-mode-code"
          className="flex-1"
        >
          {t('login.mode.code')}
        </Button>
      </div>

      {mode === 'code' ? (
        <LoginCodeForm onSignedIn={goAfterSignIn} />
      ) : (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t('login.email')} fieldId="login-email">
            <Input
              id="login-email"
              type="email"
              mono
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-email"
            />
          </FormField>
          <FormField label={t('login.password')} fieldId="login-password">
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-password"
            />
          </FormField>
          {error !== null ? (
            <p className="m-0 text-label text-bad" role="alert" data-testid="login-error">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="login-submit"
            className="mt-1 h-9 w-full"
          >
            {submitting ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>
      )}
    </AuthPageFrame>
  );
}
